// CENTRAL WEBSITE CONTENT
// Routine copy, link and contact updates should be made in this file.

const routes = {
  home: "/",
  startHere: "/start-here",
  founder: "/#founder",
  contact: "/#contact",
  memberLogin: "https://journey.soultransformationacademy.org/v2/auth/login",
  freeWebinar: "https://journey.soultransformationacademy.org/web/checkout/69a7c1a600f74e96d9cc2681",
  paidShakthi: "https://journey.soultransformationacademy.org/web/checkout/68e2f8a838086ab95cb1158f",
  terms: "/terms_and_conditions",
};

export const siteContent = {
  brand: {
    name: "The Soul Transformation Academy",
    mission: "Awaken • Heal • Transform",
    assets: {
      compactSymbol: process.env.REACT_APP_COMPACT_BRAND_ASSET,
      fullLogo: process.env.REACT_APP_FULL_BRAND_ASSET,
      fullLogoAlt: "The Soul Transformation Academy - Awaken, Heal, Transform",
    },
  },

  navigation: {
    items: [
      { label: "Home", destination: routes.home },
      { label: "Start Here", destination: routes.startHere, emphasis: true },
      { label: "About", destination: routes.founder },
      { label: "Contact", destination: routes.contact },
    ],
    memberLogin: { label: "Member Login", destination: routes.memberLogin, enabled: true },
  },

  homeMeta: {
    title: "The Soul Transformation Academy | Awareness and Practical Self-Work",
    description: "Begin with one real concern and explore a grounded path from noticing to clarity and conscious response.",
    canonical: "https://journey.soultransformationacademy.org/",
  },

  hero: {
    kicker: "START WITH WHAT IS REAL",
    heading: "Why does the same kind of problem keep coming back?",
    supportingText: [
      "Sometimes the situation changes, but the concern feels strangely familiar.",
      "Start with what is real. You do not need to know your pattern yet.",
    ],
    primaryCta: { label: "Begin Your Journey", destination: routes.startHere },
    secondaryCta: { label: "Explore the Academy", destination: "#approach" },
    topics: ["Career", "Money", "Relationships", "Inner Clarity"],
  },

  recognition: {
    heading: "Does any of this feel familiar?",
    introduction: [
      "You may be handling life well on the outside and still notice a concern that feels strangely familiar.",
      "Nothing here asks you to decide what it means. Begin by noticing.",
    ],
    items: [
      {
        title: "Career",
        heading: "The role changes. The pressure feels familiar.",
        copy: "You keep moving forward, yet overwhelm or the same response seems to return in another form.",
      },
      {
        title: "Money",
        heading: "The plan changes. The worry feels familiar.",
        copy: "Circumstances may change, but a familiar concern about money keeps returning.",
      },
      {
        title: "Relationships",
        heading: "Different conversations. A familiar reaction.",
        copy: "The people or situation may be different, yet the same distance, tension or response can seem to return.",
      },
      {
        title: "Inner Clarity",
        heading: "You keep thinking. The uncertainty returns.",
        copy: "You want to make the right choice, but the same confusion or overthinking seems to come back.",
      },
    ],
  },

  patternBridge: {
    heading: "When something feels familiar, it may be worth looking closer.",
    copy: [
      "A repeating pattern does not have to be obvious.",
      "It may begin as a concern, response or situation that seems to return in a familiar way.",
      "You do not have to name it yet.",
      "Begin by noticing.",
    ],
  },

  observation: {
    heading: "Choose one concern that is real for you.",
    copy: [
      "Observe before you interpret.",
      "You do not need to solve everything or know your pattern before you begin.",
    ],
  },

  approach: {
    kicker: "HOW THE SOUL TRANSFORMATION ACADEMY APPROACHES THE JOURNEY",
    heading: "A grounded path from noticing to choice.",
    introduction: "Awareness comes before explanation.",
    steps: [
      { title: "Notice", copy: "Begin with the concern that is real for you." },
      { title: "Observe", copy: "Look closely before deciding what it means." },
      { title: "Awareness", copy: "Notice what seems to repeat or change." },
      { title: "Clarity", copy: "See your experience with greater context." },
      { title: "Conscious Next Step", copy: "Respond with more choice and less automatic reaction." },
    ],
  },

  possibility: {
    heading: "A little more space between what happens and how you respond.",
    introduction: "Structured self-work can support a more deliberate relationship with your own experience.",
    items: [
      "Noticing before reacting",
      "Greater self-understanding",
      "Clearer, more deliberate responses",
      "Simple practical self-work",
      "A closer connection with your own experience",
    ],
  },

  community: {
    heading: "Self-work can be personal without being lonely.",
    copy: "A space to reflect, practise, ask questions and keep learning with people who are doing their own inner work too.",
    items: [
      "Shared learning",
      "Guided reflection",
      "Questions and interaction",
      "Practical practices",
      "Accountability",
      "Peer connection and belonging",
    ],
  },

  homeShakthi: {
    kicker: "START HERE · SHAKTHI AWARENESS",
    heading: "Start with one real concern.",
    copy: [
      "Shakthi Awareness is the first structured awareness experience within The Soul Transformation Academy.",
      "You do not need to know your pattern before you begin.",
      "Start with what is real, observe before you interpret and explore what may be repeating.",
    ],
    principle: "See it. Check it. Understand it. Map it.",
    cta: { label: "Explore Shakthi Awareness", destination: routes.startHere },
  },

  founder: {
    kicker: "SRINIVAS · FOUNDER AND GUIDE",
    heading: "Why I Created The Soul Transformation Academy",
    copy: [
      "I created The Soul Transformation Academy for people who sense that something in life keeps returning in a familiar way, but do not want another quick explanation or shortcut.",
      "My role is to guide a process of observation, reflection and practical self-work.",
      "Your own everyday experience remains the starting point.",
    ],
  },

  contact: {
    heading: "Stay Connected",
    intro: "Questions about The Soul Transformation Academy or your next step?",
    links: [
      { label: "Email", display: "thesoultransformationacademy@gmail.com", destination: "mailto:thesoultransformationacademy@gmail.com", icon: "mail" },
      { label: "WhatsApp", display: "WhatsApp", destination: "https://wa.me/919390070117?countrycode=91&countryName=IN&phoneNumber=919390070117", icon: "whatsapp" },
      { label: "Instagram", display: "Instagram", destination: "https://www.instagram.com/srinivas.patternbreakthrough/", icon: "instagram" },
      { label: "LinkedIn", display: "LinkedIn", destination: "https://www.linkedin.com/in/sreenivas-t-n-a29359347/", icon: "linkedin" },
      { label: "YouTube", display: "YouTube", destination: "https://www.youtube.com/@TheSoulTransformationAcade-c1r", icon: "youtube" },
      { label: "Facebook", display: "The Soul Transformation Academy on Facebook", destination: "https://www.facebook.com/profile.php?id=61570081174731", icon: "facebook" },
      { label: "Facebook Community", display: "Mid Life Healing Room", destination: "https://www.facebook.com/profile.php?id=61575218717876", icon: "facebook" },
    ],
    communityNote: "The Mid Life Healing Room is associated with the Academy's Facebook presence.",
  },

  finalInvitation: {
    heading: "You don't have to understand everything before you begin.",
    copy: ["Begin with one honest concern.", "One careful observation.", "One conscious next step."],
    cta: { label: "Begin Your Journey", destination: routes.startHere },
  },

  footer: {
    links: [{ label: "Terms", destination: routes.terms }],
    boundaryStatement: "The Soul Transformation Academy provides educational, nonclinical self-development and spiritual-wellbeing experiences. It does not replace qualified medical, psychological, legal or financial care.",
    copyright: "© 2026 The Soul Transformation Academy",
  },

  termsPage: {
    meta: {
      title: "Terms & Conditions | The Soul Transformation Academy",
      description: "Terms and conditions governing access to programmes, memberships, webinars, consultations and digital offerings from The Soul Transformation Academy.",
      canonical: "https://journey.soultransformationacademy.org/terms_and_conditions",
    },
    title: "Terms & Conditions",
    effectiveDate: "01 Jan 2026",
    introduction: [
      "These Terms & Conditions govern your access to and use of all services, programmes, content and platforms operated by The Soul Transformation Academy, referred to in these Terms as the Academy, we, us or our.",
      "By accessing, enrolling in, purchasing or participating in any offering from the Academy, you agree to be bound by these Terms. If you do not agree, please do not use our services.",
    ],
    sections: [
      {
        heading: "1. About The Soul Transformation Academy",
        facts: [
          ["Official Business Name", "The Soul Transformation Academy"],
          ["Primary Platform", "TagMango, for learning and community delivery"],
          ["Contact Email", "thesoultransformationacademy@gmail.com"],
        ],
        paragraphs: ["The Soul Transformation Academy provides spiritual education, awareness programmes, energy-based insights and wellness-oriented content rooted in traditional knowledge systems, reflection practices and self-work methodologies."],
      },
      {
        heading: "2. Scope of These Terms",
        paragraphs: ["These Terms apply to current programmes, memberships, webinars, consultations and digital offerings made available by the Academy, including but not limited to:"],
        bullets: [
          "Awareness journeys",
          "Courses, webinars, workshops and masterclasses",
          "Community groups on services such as WhatsApp, TagMango and Telegram",
          "Aura reading services",
          "Energy or spiritual consultations",
          "Healing or remedial guidance sessions",
          "Digital content, recordings, worksheets and downloads",
        ],
      },
      {
        heading: "3. Nature of Services",
        subheading: "Educational and Spiritual Nature",
        paragraphs: ["All services offered by the Academy are educational, spiritual and awareness-based in nature.", "They are not:"],
        bullets: ["medical treatment", "psychological therapy", "psychiatric care", "legal advice", "financial or investment advice", "emergency or crisis services"],
        secondarySubheading: "No Medical or Mental Health Claims",
        secondaryParagraphs: ["The Academy does not diagnose, treat, cure or prevent any medical or mental health condition.", "You must seek help from qualified professionals if you have:"],
        secondaryBullets: ["physical illness", "mental health challenges", "emotional distress", "trauma", "addiction", "suicidal thoughts", "financial or legal emergencies"],
      },
      {
        heading: "4. Participation Is Voluntary and Self-Responsibility",
        paragraphs: ["By participating, you acknowledge that:"],
        bullets: [
          "Your participation is voluntary",
          "You are responsible for your choices, actions, interpretations and outcomes",
          "Results vary significantly between individuals",
          "No specific outcome is guaranteed",
        ],
        closing: ["The Academy is not responsible for decisions you make based on programme content."],
      },
      {
        heading: "5. No Guarantees or Outcome Promises",
        paragraphs: ["The Soul Transformation Academy does not guarantee:"],
        bullets: ["healing", "transformation", "financial improvement", "relationship outcomes", "health improvement", "spiritual awakening", "emotional resolution"],
        closing: ["Any examples, stories or testimonials are individual experiences, not promises or assurances."],
      },
      {
        heading: "6. Eligibility",
        bullets: [
          "The Soul Transformation Academy services are intended for adults aged 18 and above",
          "By enrolling, you confirm that you are legally capable of entering into this agreement",
          "We do not knowingly provide services to minors",
        ],
      },
      {
        heading: "7. Payments, Pricing, Refunds and Cancellations",
        subheading: "Pricing",
        bullets: [
          "Prices are clearly displayed before purchase",
          "Pricing reflects the access, structure, platform costs and delivery of the current offering",
        ],
        secondarySubheading: "Refunds and Cancellations",
        secondaryParagraphs: [
          "Except where applicable law requires otherwise, purchases of digital programmes, awareness journeys, memberships, webinars, consultations and other services are generally non-refundable once digital access has been granted or service delivery has begun.",
          "Nothing in these Terms limits any refund, cancellation or other remedy that a consumer is entitled to under applicable law.",
        ],
      },
      {
        heading: "8. Programmes and Memberships",
        paragraphs: ["Benefits and access vary across current programmes, memberships, webinars, consultations and digital offerings made available by the Academy. They may include:"],
        bullets: ["exclusive content", "live or recorded sessions", "community access", "priority support", "optional services such as aura reading"],
        secondaryParagraphs: ["The Academy reserves the right to:"],
        secondaryBullets: ["modify programme or membership structures", "update content", "change schedules", "upgrade platforms"],
        closing: ["Access is non-transferable."],
      },
      {
        heading: "9. Aura Reading, Photos and Date of Birth",
        paragraphs: ["Some services may optionally request a full-size photograph and date of birth.", "By submitting these, you confirm that:"],
        bullets: [
          "You provide the information voluntarily",
          "You understand it is used for spiritual or energy interpretation only",
          "You accept that interpretations are subjective and are not factual claims",
        ],
        secondaryParagraphs: ["The Academy does not guarantee accuracy, does not use such data for advertising without explicit consent and handles such data with higher confidentiality."],
      },
      {
        heading: "10. Intellectual Property",
        paragraphs: ["All Academy content is protected by copyright, including:"],
        bullets: ["videos", "audios", "texts", "worksheets", "meditations", "frameworks", "programme structures"],
        secondaryParagraphs: ["You may use content for personal use only. You may not:"],
        secondaryBullets: ["copy", "redistribute", "resell", "record live sessions", "share login access", "create derivative commercial products"],
        closing: ["Violations may result in termination of access."],
      },
      {
        heading: "11. Community Conduct",
        paragraphs: ["In Academy communities, you agree to:"],
        bullets: ["maintain respectful communication", "avoid harassment, hate or abuse", "avoid unsolicited promotion or spam", "respect the privacy of other members"],
        secondaryParagraphs: ["The Academy reserves the right to:"],
        secondaryBullets: ["mute", "remove", "suspend", "permanently ban members for violations"],
      },
      {
        heading: "12. Limitation of Liability",
        paragraphs: ["To the maximum extent permitted by law, the Academy shall not be liable for:"],
        bullets: ["indirect or consequential damages", "emotional distress", "loss of income", "personal decisions", "third-party platform issues", "misuse or misinterpretation of content"],
        closing: ["Your use of Academy services is at your own risk."],
      },
      {
        heading: "13. Third-Party Platforms",
        paragraphs: ["The Academy uses third-party platforms including:"],
        bullets: ["TagMango", "Meta services such as Facebook and Instagram", "payment gateways", "email and WhatsApp services"],
        secondaryParagraphs: ["The Academy is not responsible for outages, data practices, policy changes or technical issues of these platforms."],
      },
      {
        heading: "14. Termination of Access",
        paragraphs: ["The Academy may suspend or terminate access if:"],
        bullets: ["these Terms are violated", "misuse or abuse occurs", "fraudulent activity is suspected", "platform rules are breached"],
        closing: ["Any refund or cancellation rights remain subject to Section 7 and applicable law."],
      },
      {
        heading: "15. Governing Law and Jurisdiction",
        paragraphs: ["These Terms are governed by the laws of India. Subject to applicable law, disputes shall be subject to the jurisdiction of the competent courts in Hyderabad, Telangana, India."],
      },
      {
        heading: "16. Changes to Terms",
        paragraphs: ["The Academy may update these Terms from time to time."],
        bullets: ["Updates will be posted on this page", "Continued use constitutes acceptance of revised Terms"],
      },
      {
        heading: "17. Business and Contact Information",
        facts: [
          ["Business Name", "The Soul Transformation Academy"],
          ["Website", "soultransformationacademy.org"],
          ["Email", "thesoultransformationacademy@gmail.com"],
          ["Location", "Hyderabad, Telangana, India"],
        ],
      },
    ],
  },

  startHerePage: {
    meta: {
      title: "Shakthi Awareness | The Soul Transformation Academy",
      description: "Start with one real concern through a structured 9-day awareness journey beginning with a short Day 0 preparation.",
      canonical: "https://journey.soultransformationacademy.org/start-here",
    },
    hero: {
      kicker: "SHAKTHI AWARENESS",
      heading: "Start with one real concern.",
      copy: [
        "Shakthi Awareness is a structured 9-day awareness journey beginning with a short Day 0 preparation.",
        "You do not need to know your pattern before you begin.",
      ],
      topics: ["Career", "Money", "Relationships", "Inner Clarity"],
    },
    introduction: {
      heading: "Observation before interpretation.",
      copy: [
        "Sometimes we want an explanation before we have looked carefully at what is actually happening.",
        "Shakthi Awareness begins differently.",
        "You start with one real concern from your own life.",
        "You observe it.",
        "You check what seems familiar or repeating.",
        "You begin to understand the context around it.",
        "Then you map what you notice.",
      ],
    },
    journey: {
      heading: "See it. Check it. Understand it. Map it.",
      copy: [
        "Day 0 preparation + Days 1 to 9",
        "The detailed journey unfolds inside the programme.",
        "You are not expected to know the answer before you begin.",
      ],
    },
    explore: {
      heading: "What you may explore",
      copy: "You may use the journey to look more carefully at concerns related to:",
      items: ["Career", "Money", "Relationships", "Inner Clarity"],
      closing: ["The aim is not to force an explanation.", "It is to help you notice more clearly."],
    },
    boundaries: {
      heading: "What this experience is not",
      copy: [
        "Shakthi Awareness is an educational self-awareness experience.",
        "It is not therapy, medical care, psychological treatment, legal advice or financial advice.",
        "It does not promise miracles, instant transformation or guaranteed outcomes.",
        "If your concern requires urgent, medical, psychological, legal, financial or other specialised support, that support should take priority.",
      ],
    },
    webinar: {
      heading: "Want to understand the approach before you begin?",
      copy: "Join a free introductory webinar and explore how The Soul Transformation Academy approaches awareness, repeating concerns and conscious responses.",
      cta: { label: "Register for the Free Webinar", destination: routes.freeWebinar },
    },
    paid: {
      heading: "Ready to begin the experience?",
      supportingLine: "Start with what is real.",
      copy: "You do not have to understand everything before you begin.",
      cta: { label: "Begin Shakthi Awareness", destination: routes.paidShakthi },
    },
  },
};